import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Clock, LogOut, RefreshCw } from 'lucide-react';

// Aviso aparece quando restam <= 30 minutos
const WARN_THRESHOLD_SECONDS = 30 * 60;
// Intervalo de verificação: 60 segundos
const CHECK_INTERVAL_MS = 60 * 1000;

function decodeTokenExp(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ?? null; // Unix timestamp em segundos
  } catch {
    return null;
  }
}

function formatCountdown(seconds) {
  if (seconds <= 0) return '0 min';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m > 0) return `${m} min${s > 0 ? ` ${s}s` : ''}`;
  return `${s}s`;
}

export default function SessionWarning() {
  const { token, isAuthenticated, logout, renewSession } = useAuth();
  const [show, setShow] = useState(false);
  const [remaining, setRemaining] = useState(0); // segundos restantes
  const [renewing, setRenewing] = useState(false);

  const checkExpiry = useCallback(() => {
    if (!isAuthenticated || !token) return;
    const exp = decodeTokenExp(token);
    if (!exp) return;

    const nowSec = Math.floor(Date.now() / 1000);
    const diff = exp - nowSec;

    if (diff <= 0) {
      // Token já expirou — logout silencioso
      logout();
      return;
    }

    setRemaining(diff);

    if (diff <= WARN_THRESHOLD_SECONDS) {
      setShow(true);
    }
  }, [token, isAuthenticated, logout]);

  // Verificação periódica
  useEffect(() => {
    checkExpiry(); // verifica na montagem
    const interval = setInterval(checkExpiry, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [checkExpiry]);

  // Countdown visual de 1 em 1 segundo quando o modal está aberto
  useEffect(() => {
    if (!show) return;
    const tick = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(tick);
          logout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [show, logout]);

  const handleRenew = async () => {
    setRenewing(true);
    const ok = await renewSession();
    setRenewing(false);
    if (ok) {
      setShow(false);
      toast.success('Sessão renovada! Você está conectado por mais 7 dias.');
    } else {
      toast.error('Não foi possível renovar a sessão. Por favor, faça login novamente.');
      logout();
    }
  };

  const handleLogout = () => {
    setShow(false);
    logout();
  };

  if (!show || !isAuthenticated) return null;

  const isUrgent = remaining <= 5 * 60; // menos de 5 minutos → urgente

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Barra de progresso no topo */}
        <div className="h-1 w-full bg-slate-100">
          <div
            className={`h-1 transition-all duration-1000 ${isUrgent ? 'bg-red-500' : 'bg-amber-400'}`}
            style={{
              width: `${Math.min(100, (remaining / WARN_THRESHOLD_SECONDS) * 100)}%`,
            }}
          />
        </div>

        <div className="p-6">
          {/* Ícone + título */}
          <div className="flex items-start gap-4 mb-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isUrgent ? 'bg-red-100' : 'bg-amber-100'}`}>
              <Clock className={`w-5 h-5 ${isUrgent ? 'text-red-600' : 'text-amber-600'}`} />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Sessão prestes a expirar
              </h3>
              <p className="text-slate-500 text-sm mt-0.5">
                Sua sessão expira em{' '}
                <span className={`font-bold ${isUrgent ? 'text-red-600' : 'text-amber-600'}`}>
                  {formatCountdown(remaining)}
                </span>
              </p>
            </div>
          </div>

          <p className="text-sm text-slate-500 mb-5">
            Clique em <strong className="text-slate-700">Continuar conectado</strong> para renovar sua sessão sem
            precisar fazer login novamente.
          </p>

          {/* Botões */}
          <div className="flex flex-col gap-2">
            <button
              onClick={handleRenew}
              disabled={renewing}
              className="w-full flex items-center justify-center gap-2 bg-[#0147a0] hover:bg-[#003375] text-white font-semibold py-2.5 rounded-xl text-sm transition-colors disabled:opacity-60"
            >
              <RefreshCw className={`w-4 h-4 ${renewing ? 'animate-spin' : ''}`} />
              {renewing ? 'Renovando...' : 'Continuar conectado'}
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 border border-slate-200 text-slate-600 hover:bg-slate-50 font-medium py-2.5 rounded-xl text-sm transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sair agora
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
